<template>
  <div class="sale-target-list">
    <!-- 销售目标（企业微信手机端，仅查看）：显示当前周期达成率，点击查看对应合同 -->
    <mobile-list-header>
      <!-- 第一行：搜索框占满整行，左输入框 + 右搜索按钮 -->
      <div class="search-bar">
        <div class="search-input">
          <yq-icon class="icon" name="element-ui_search"/>
          <input v-model="searchInfo.searchKey" placeholder="目标名称/目标编号" @keyup.enter="resetSearch"/>
          <yq-icon v-if="searchInfo.searchKey" class="icon clear" name="element-ui_close" @click="clearKey"/>
        </div>
        <div class="search-btn" @click="resetSearch">搜索</div>
      </div>
      <!-- 第二行：查询条件，无背景色，超过一屏横向滑动 -->
      <div class="filter-row">
        <mobile-condition-select v-model="searchInfo.targetYear" :data="yearOptions"
                                 label-column="label" code-column="value" type="list"
                                 placeholder="年度" @change="resetSearch"/>
        <mobile-search-select v-if="isAdmin" v-model="searchInfo.employeeId" :query="queryEmployeePage" size="small"
                              label-column="employeeName" code-column="id"
                              :display-text="searchInfo.employeeName" placeholder="销售人员"
                              search-placeholder="职员名称/手机号" @change="onEmployeeChange"/>
        <mobile-condition-select v-model="searchInfo.targetType" :data="targetTypeOptions"
                                 label-column="label" code-column="value" type="list"
                                 placeholder="目标维度" @change="resetSearch"/>
        <mobile-condition-select v-model="searchInfo.businessType" :data="businessTypeOptions"
                                 label-column="label" code-column="value" type="list"
                                 placeholder="业务类型" @change="resetSearch"/>
      </div>
    </mobile-list-header>

    <mobile-list :loading="loading" :has-more="searchInfo.hasMore" :query-list="queryList"
                 :refresh="resetSearch" ref="mobileList">
      <list-card class="target-item" v-for="item in dataList" :key="item.id"
                 :label="item.employeeName || '—'" @click="toContractList(item)">
        <template #desc>
          <span class="type-tag">{{ item.targetTypeName }}</span>
          <span class="business-tag">{{ item.businessTypeName }}</span>
          <yq-icon class="item-arrow" name="element-ui_arrow-right"/>
        </template>
        <data-column label="目标名称">{{ item.targetName || "—" }}</data-column>
        <data-column label="目标值">{{ metric(item.targetAmount, item.unit) }} {{ item.unit }}</data-column>
        <data-column label="已达成">{{ metric(item.achievedAmount, item.unit) }} {{ item.unit }}</data-column>
        <data-column label="达成率">
          <span class="rate-text" :class="{done: Number(item.achievementRate) >= 100}">
            {{ item.achievementRate || 0 }}%
          </span>
        </data-column>
        <div class="rate-track">
          <i :style="{width: ratePercent(item.achievementRate) + '%'}"/>
        </div>
      </list-card>
    </mobile-list>
  </div>
</template>

<script>

import {useRoute, useRouter} from "vue-router";
import MobileList from "@/components/qywx/layout/MobileList.vue";
import MobileListHeader from "@/components/qywx/layout/MobileListHeader.vue";
import MobileConditionSelect from "@/components/qywx/data/MobileConditionSelect.vue";
import MobileSearchSelect from "@/components/qywx/data/MobileSearchSelect.vue";
import ListCard from "@/components/qywx/layout/ListCard.vue";
import DataColumn from "@/components/qywx/data/DataColumn.vue";
import YqIcon from "@/components/YqIcon.vue";
import saleTargetApi from "@/api/dz-crm/sale/saleTargetApi";
import commonApi from "@/api/commonApi";
import Util from "@/util/util";

const TARGET_TYPE_OPTIONS = [
  {value: 1, label: "年度目标"},
  {value: 2, label: "季度目标"},
  {value: 3, label: "月度目标"},
];
const BUSINESS_TYPE_OPTIONS = [
  {value: 1, label: "毛利"},
  {value: 2, label: "新增客户"},
  {value: 3, label: "客户转介绍"},
  {value: 4, label: "服务费"},
];

export default {
  setup() {
    return {
      router: useRouter(),
      route: useRoute(),
    }
  },
  name: "SaleTargetListQW",
  components: {
    YqIcon, DataColumn, ListCard, MobileSearchSelect, MobileConditionSelect, MobileListHeader, MobileList
  },
  data() {
    const year = new Date().getFullYear();
    const isAdmin = Number(Util.getRoleType()) === 1;
    return {
      isAdmin,
      loading: false,
      searchInfo: {
        searchKey: "",
        targetYear: year,
        employeeId: isAdmin ? "" : Util.getEmployeeId(),
        employeeName: "",
        targetType: "",
        businessType: "",
        pageNumber: 0,
        pageSize: 20,
        hasMore: true, // 初始为 true，列表挂载后由无限滚动自动触发首次查询
      },
      dataList: [],
      yearOptions: Array.from({length: 7}, (_, i) => ({
        value: year - 5 + i,
        label: (year - 5 + i) + "年",
      })),
      targetTypeOptions: TARGET_TYPE_OPTIONS,
      businessTypeOptions: BUSINESS_TYPE_OPTIONS,
    };
  },
  methods: {
    queryList() {
      this.loading = true;
      this.searchInfo.pageNumber++;
      // 普通角色后端会锁死本人，这里也带上本人，避免筛选条件看起来是「全部」
      if (!this.isAdmin) {
        this.searchInfo.employeeId = Util.getEmployeeId();
      }
      saleTargetApi.queryList({
        ...this.cleanParams(),
        pageNumber: this.searchInfo.pageNumber,
        pageSize: this.searchInfo.pageSize,
      }).then(res => {
        this.dataList = this.dataList.concat(res.records || []);
        this.searchInfo.hasMore = res.pageCount > this.searchInfo.pageNumber;
        this.loading = false;
        this.$refs.mobileList.hideRefreshTitle();
      }).catch(() => {
        this.loading = false;
      })
    },
    resetSearch() {
      this.searchInfo.pageNumber = 0;
      this.dataList = [];
      this.searchInfo.hasMore = true;
      this.queryList();
    },
    clearKey() {
      this.searchInfo.searchKey = "";
      this.resetSearch();
    },
    /** 空值不提交；employeeName 只用于回显 */
    cleanParams() {
      const params = {};
      Object.keys(this.searchInfo).forEach(key => {
        const value = this.searchInfo[key];
        if (value === "" || value === null || value === undefined) {
          return;
        }
        if (key === "pageNumber" || key === "pageSize" || key === "hasMore" || key === "employeeName") {
          return;
        }
        params[key] = value;
      });
      return params;
    },
    onEmployeeChange(record) {
      this.searchInfo.employeeName = record ? (record.employeeName || "") : "";
      this.resetSearch();
    },
    queryEmployeePage(params) {
      return commonApi.queryEmployeeListByPage(params);
    },
    // ---------- 跳转合同 ----------
    /**
     * 点击目标：按销售人员与目标周期跳到合同列表。
     * 新增客户/转介绍目标也按同样的销售人员与时间范围跳转，统一从合同侧核对数据。
     */
    toContractList(item) {
      if (!item.employeeId) {
        return;
      }
      const range = this.targetRange(item);
      this.router.push({
        path: "/qywx/contract/list",
        query: {
          salesId: item.employeeId,
          salesName: item.employeeName || "",
          startTime: range.start,
          endTime: range.end,
        },
      });
    },
    /** 目标周期换算成签约日期区间：年度整年 / 季度三个月 / 月份当月 */
    targetRange(item) {
      const pad = n => String(n).padStart(2, "0");
      const lastDay = (year, month) => new Date(year, month, 0).getDate();
      const year = Number(item.targetYear) || new Date().getFullYear();
      if (item.targetType === 2) {
        const quarter = Number(item.targetQuarter) || 1;
        const startMonth = quarter * 3 - 2;
        const endMonth = quarter * 3;
        return {
          start: `${year}-${pad(startMonth)}-01`,
          end: `${year}-${pad(endMonth)}-${pad(lastDay(year, endMonth))}`,
        };
      }
      if (item.targetType === 3) {
        const month = Number(item.targetMonth) || 1;
        return {
          start: `${year}-${pad(month)}-01`,
          end: `${year}-${pad(month)}-${pad(lastDay(year, month))}`,
        };
      }
      return {start: `${year}-01-01`, end: `${year}-12-31`};
    },
    // ---------- 展示 ----------
    ratePercent(rate) {
      return Math.max(0, Math.min(100, Number(rate) || 0));
    },
    metric(value, unit) {
      if (value === null || value === undefined || value === "") {
        return unit === "个" ? "0" : "0.00";
      }
      return Number(value).toLocaleString("zh-CN", {
        minimumFractionDigits: unit === "个" ? 0 : 2,
        maximumFractionDigits: unit === "个" ? 0 : 2,
      });
    }
  }
};
</script>

<style lang="scss" scoped>
.sale-target-list{
  height: 100%;
  display: flex;
  flex-direction: column;
}
// 第一行：搜索框占满整行，左输入框 + 右搜索按钮
.search-bar{
  display: flex;
  align-items: center;
  height: 44px;
  padding-left: 12px;
  border-radius: 999px;
  background: var(--yq-background-color);
  overflow: hidden;
  .search-input{
    flex: 1;
    display: flex;
    align-items: center;
    min-width: 0;
    height: 100%;
    padding-right: 12px;
    .icon{
      flex: none;
      color: var(--yq-font-color-desc);
      font-size: var(--yq-font-size-mini-mobile);
      &.clear{
        margin-left: 6px;
      }
    }
    input{
      flex: 1;
      min-width: 0;
      height: 100%;
      margin-left: 6px;
      border: none;
      outline: none;
      background: transparent;
      font-size: var(--yq-font-size-mini-mobile);
      color: var(--yq-font-color-base);
      &::placeholder{
        color: var(--yq-font-color-desc);
      }
    }
  }
  .search-btn{
    flex: none;
    display: flex;
    align-items: center;
    height: 100%;
    padding: 0 18px;
    background: var(--el-color-primary);
    color: #FFF;
    font-size: var(--yq-font-size-mini-mobile);
  }
}
// 第二行：查询条件，无背景色，超过一屏横向滑动
.filter-row{
  display: flex;
  gap: 18px;
  margin-top: 10px;
  overflow-x: auto;
  scrollbar-width: none;
  &::-webkit-scrollbar{
    display: none;
  }
  > *{
    flex: none;
  }
}
.target-item{
  :deep(.card-title .desc){
    display: flex;
    align-items: center;
    gap: 6px;
  }
  .type-tag,
  .business-tag{
    flex: none;
    padding: 2px 8px;
    border-radius: 4px;
    font-size: var(--yq-font-size-mini-mobile);
  }
  .type-tag{
    color: var(--el-color-primary);
    background: var(--el-color-primary-light-9);
  }
  .business-tag{
    color: var(--el-color-success);
    background: var(--el-color-success-light-9);
  }
  .item-arrow{
    color: var(--yq-font-color-desc);
    font-size: var(--yq-font-size-mini-mobile);
  }
  .rate-text{
    font-weight: 600;
    color: var(--el-color-warning);
    font-variant-numeric: tabular-nums;
    &.done{
      color: var(--el-color-success);
    }
  }
  .rate-track{
    height: 6px;
    margin-top: 6px;
    border-radius: 3px;
    background: var(--yq-background-color);
    overflow: hidden;
    i{
      display: block;
      height: 100%;
      border-radius: 3px;
      background: var(--el-color-primary);
      transition: width .3s;
    }
  }
}
</style>
